import type { IconName } from "components/Icon";

import { Stack } from "@mui/material";
import { FilledCheckbox } from "components/Checkbox";
import { Icon } from "components/Icon";
import { IconButton } from "components/IconButton";
import { useTranslation } from "react-i18next";
import { customApiCall } from "utils/axios";

import type { TransientResultFile } from "../lib/formatJobResults";

import { transientJobUrls } from "../api/urls";
import { downloadFromUrl } from "../lib/downloadFromUrl";
import { formatResultFileName, resultFileKey } from "../lib/formatJobResults";

type FileIcon = {
  color: string;
  iconName: IconName;
};

type TransientJobResultRoomListProps = {
  className?: string;
  emptyLabel?: string;
  fileIcon?: (file: TransientResultFile) => FileIcon;
  fileKey?: (file: TransientResultFile) => string;
  files: TransientResultFile[];
  folderName: string;
  isSelected: (key: string) => boolean;
  listClassName?: string;
  onSetMany: (keys: string[], shouldSelect: boolean) => void;
  onToggle: (key: string) => void;
  search: string;
};

const JSON_ICON_COLOR = "var(--color-purpose-certificate-gold)";
const defaultFileIcon = (): FileIcon => ({ color: JSON_ICON_COLOR, iconName: "Json" });

const downloadFile = (file: TransientResultFile, errorMessage: string) => {
  if (file.path) {
    customApiCall.download({
      errorMessage,
      fileName: formatResultFileName(file.name),
      url: transientJobUrls.fileDownload(file.path),
    });
    return;
  }
  if (file.url) downloadFromUrl(file.url, file.name);
};

export const TransientJobResultRoomList = ({
  className,
  emptyLabel,
  fileIcon = defaultFileIcon,
  fileKey: fileKeyFromProps,
  files,
  folderName,
  isSelected,
  listClassName = "max-h-[40vh] overflow-auto",
  onSetMany,
  onToggle,
  search,
}: TransientJobResultRoomListProps) => {
  const { t } = useTranslation();
  const errorMessage = String(t("general.downloadFileProblem"));
  const query = search.trim().toLowerCase();
  const visibleFiles = query
    ? files.filter((file) => formatResultFileName(file.name).toLowerCase().includes(query))
    : files;
  const fileKey = fileKeyFromProps ?? ((file: TransientResultFile) => resultFileKey(folderName, file.name));
  const keys = visibleFiles.map(fileKey);
  const allSelected = keys.length > 0 && keys.every(isSelected);

  return (
    <div className={["rounded-ds-0-5x bg-lds-surface-muted p-3 border-ds-subtle", className].filter(Boolean).join(" ")}>
      <div className="mb-2 shrink-0 pb-2 border-b-ds-subtle">
        <FilledCheckbox
          checked={allSelected}
          disabled={keys.length === 0}
          onChange={() => onSetMany(keys, !allSelected)}
        >
          <span className="capitalize">{t("all")}</span>
        </FilledCheckbox>
      </div>
      <div className={listClassName}>
        {visibleFiles.length === 0 ? (
          <p className="font-ds-body-regular-sm py-2 text-lds-foreground-muted">
            {emptyLabel ?? t("Execution.transient.jobDetails.noMatchingRooms")}
          </p>
        ) : (
          visibleFiles.map((file) => {
            const key = fileKey(file);
            const icon = fileIcon(file);
            return (
              <Stack className="w-full min-w-0 justify-between gap-2 py-1" key={file.path || file.name}>
                <FilledCheckbox checked={isSelected(key)} className="min-w-0 gap-1" onChange={() => onToggle(key)}>
                  <Stack className="min-w-0 gap-1">
                    <Icon color={icon.color} iconName={icon.iconName} size="lg" />
                    <span className="truncate text-lds-foreground-primary font-ds-body-regular-md">
                      {formatResultFileName(file.name)}
                    </span>
                  </Stack>
                </FilledCheckbox>
                <IconButton
                  buttonProps={{
                    className: "shrink-0 mr-1",
                    onClick: (event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      downloadFile(file, errorMessage);
                    },
                    size: "sm",
                    variant: "tertiary",
                  }}
                  iconProps={{
                    color: "var(--color-element-primary-initial)",
                    iconName: "Download",
                    size: "md",
                  }}
                />
              </Stack>
            );
          })
        )}
      </div>
    </div>
  );
};
